import type { EightBallState, EightBallRack, EightBallFoulType } from './types';

export function createEightBallState(
  player1Name: string,
  player2Name: string,
  racksTarget: number
): EightBallState {
  return {
    players: [
      { name: player1Name, racksWon: 0 },
      { name: player2Name, racksWon: 0 },
    ],
    racksTarget,
    rackNumber: 1,
    completedRacks: [],
    gameOver: false,
    winnerId: null,
  };
}

export interface RackWinInput {
  winnerIndex: 0 | 1;
  foulType?: EightBallFoulType;
  durationSeconds?: number;
  player1Balls?: number | null;
  player2Balls?: number | null;
}

export function recordRackWin(state: EightBallState, input: RackWinInput): EightBallState {
  if (state.gameOver) return state;

  const loserIndex: 0 | 1 = input.winnerIndex === 0 ? 1 : 0;
  const rack: EightBallRack = {
    rackNumber: state.rackNumber,
    winnerIndex: input.winnerIndex,
    winnerName: state.players[input.winnerIndex].name,
    loserName: state.players[loserIndex].name,
    foulType: input.foulType,
    durationSeconds: input.durationSeconds,
    player1Balls: input.player1Balls,
    player2Balls: input.player2Balls,
  };

  const newPlayers: EightBallState['players'] = [
    { ...state.players[0] },
    { ...state.players[1] },
  ];
  newPlayers[input.winnerIndex] = {
    ...newPlayers[input.winnerIndex],
    racksWon: newPlayers[input.winnerIndex].racksWon + 1,
  };

  const racksNeeded = state.racksTarget > 0 ? Math.ceil((state.racksTarget + 1) / 2) : null;
  const gameOver =
    racksNeeded !== null &&
    (newPlayers[0].racksWon >= racksNeeded || newPlayers[1].racksWon >= racksNeeded);

  const winnerId: 0 | 1 | null = gameOver
    ? (newPlayers[0].racksWon > newPlayers[1].racksWon ? 0 : 1)
    : null;

  return {
    ...state,
    players: newPlayers,
    rackNumber: state.rackNumber + 1,
    completedRacks: [...state.completedRacks, rack],
    gameOver,
    winnerId,
  };
}

export function undoLastRack(state: EightBallState): EightBallState {
  if (state.completedRacks.length === 0) return state;

  const lastRack = state.completedRacks[state.completedRacks.length - 1];
  const newPlayers: EightBallState['players'] = [
    { ...state.players[0] },
    { ...state.players[1] },
  ];
  newPlayers[lastRack.winnerIndex] = {
    ...newPlayers[lastRack.winnerIndex],
    racksWon: newPlayers[lastRack.winnerIndex].racksWon - 1,
  };

  return {
    ...state,
    players: newPlayers,
    rackNumber: state.rackNumber - 1,
    completedRacks: state.completedRacks.slice(0, -1),
    gameOver: false,
    winnerId: null,
  };
}

export const FOUL_LABELS: Record<EightBallFoulType, string> = {
  scratch:     'Scratch',
  wrong_ball:  'Wrong Ball',
  early_8:     'Early 8-Ball',
  scratch_on_8:'Scratch on 8',
  other:       'Foul',
};
